import { CustomerAddressElementDataType } from '@/components/sections/builder/elements/body/customer/customer_address/type';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { isEmpty, normalizePinpoint } from '@/lib/utils';
import { BaseCustomerAddress } from '@/types/http/customer/common';
import { Loader2, MapPin, Trash2 } from 'lucide-react';

type ModalCustomerAddressProps = {
  isShow: boolean;
  setIsShow: React.Dispatch<React.SetStateAction<boolean>>;
  data: { mode: 'add' | 'edit'; data?: BaseCustomerAddress } | undefined;
  setData: React.Dispatch<
    React.SetStateAction<{ mode: 'add' | 'edit'; data?: BaseCustomerAddress } | undefined>
  >;
  close: () => void;
  open: (pData?: { mode: 'add' | 'edit'; data?: BaseCustomerAddress } | undefined) => void;
};

type CardAddressProps = {
  address: BaseCustomerAddress;
  content: CustomerAddressElementDataType;
  isDefault?: boolean;
  isLoading: boolean;
  modalCustomer: ModalCustomerAddressProps;
  handleDefaultAddress?: (addressId: string) => void;
  handleDeleteAddress?: (addressId: string) => void;
};

export default function CardAddress({
  address,
  content,
  isDefault = false,
  isLoading,
  modalCustomer,
  handleDefaultAddress,
  handleDeleteAddress,
}: CardAddressProps) {
  const pinpoint = normalizePinpoint(address.pinpoint);

  return (
    <Card
      className={isDefault ? 'border-l-4' : ''}
      style={{ borderLeftColor: isDefault ? content.colorScheme?.btnSolidBackgroud : undefined }}
    >
      <CardContent className='p-4'>
        <div className='flex items-start justify-between gap-3'>
          <div className='min-w-0 flex-1 text-sm'>
            <h4 className='truncate font-medium'>
              {address.first_name} {address.last_name}
            </h4>
            <p className='text-sm text-muted-foreground'>{address.phone}</p>
            <p className='mt-1 line-clamp-1 text-sm'>{address.address}</p>
            {!isEmpty(pinpoint) && (
              <div
                className='mt-2 flex items-center gap-1'
                style={{
                  color: content.colorScheme?.btnSolidBackgroud,
                }}
              >
                <MapPin className='h-4 w-4' />
                <span className='text-sm'>Pinpoint Added</span>
              </div>
            )}
          </div>
          {isDefault && (
            <Badge
              style={{
                backgroundColor: content.colorScheme?.btnSolidBackgroud,
                color: content.colorScheme?.btnSolidlabel,
              }}
            >
              Default
            </Badge>
          )}
        </div>
        <div className='mt-4 flex gap-2'>
          <Button
            variant='outline'
            size='sm'
            disabled={isLoading}
            onClick={() => {
              modalCustomer.open({
                mode: 'edit',
                data: address as BaseCustomerAddress,
              });
            }}
            data-testid='edit-address-customer'
          >
            {isLoading ? <Loader2 className='h-4 w-4 animate-spin' /> : 'Edit Address'}
          </Button>
          {!isDefault && handleDefaultAddress && (
            <Button
              variant='outline'
              size='sm'
              className=''
              style={{
                backgroundColor: content.colorScheme?.btnSolidBackgroud,
                color: content.colorScheme?.btnSolidlabel,
              }}
              onClick={() => handleDefaultAddress(address.id as string)}
              data-testid='set-default-address-customer'
              disabled={isLoading}
            >
              {isLoading ? <Loader2 className='h-4 w-4 animate-spin' /> : 'Set as default'}
            </Button>
          )}
          {handleDeleteAddress && (
            <Button
              variant='outline'
              size='sm'
              className='text-red-500'
              onClick={() => handleDeleteAddress(address.id as string)}
              disabled={isLoading}
              data-tesid='delete-address-customer'
            >
              {isLoading ? (
                <Loader2 className='h-4 w-4 animate-spin' />
              ) : (
                <Trash2 className='h-4 w-4' />
              )}
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
